// ChatApp — the chat screen below the top bar: projects on the left, the conversation in
// the middle, the project's files on the right. Owns the stream for the open project.
//
// Props: pid (from the route, may be empty), onWsState(sleep_state) so the top bar's orb
// follows the workspace.

import { h, Component } from 'preact';
import { route } from 'preact-router';
import {
  listProjects, createProject, getProject, updateProject, archiveProject, sendMessage, cancelTurn,
  answerCallback, streamProject, getWorkspace, wakeWorkspace,
} from '../api.js';
import { Feed } from './feed.js';
import { Composer } from './composer.js';
import { FilesPanel } from './files.js';
import { Icon } from './icons.js';
import { fromHistory, applyEvent, markAnswered } from './transcript.js';
import './ui.css';

const BUSY = new Set(['running', 'waking', 'waiting']);
const titleFrom = (text) => {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > 48 ? t.slice(0, 46).replace(/\s\S*$/, '') + '…' : (t || 'New chat');
};

function groupProjects(list) {
  const now = Date.now(), day = 86400000;
  const groups = [['Today', []], ['Previous 7 days', []], ['Older', []]];
  for (const p of list) {
    const t = Date.parse((p.updated_at || p.created_at || '') + (/(Z|[+-]\d\d:?\d\d)$/.test(p.updated_at || '') ? '' : 'Z'));
    const age = now - (isNaN(t) ? 0 : t);
    groups[age < day ? 0 : age < 7 * day ? 1 : 2][1].push(p);
  }
  return groups.filter(([, xs]) => xs.length);
}

export class ChatApp extends Component {
  constructor(p) {
    super(p);
    this.state = {
      projects: null, project: null, items: [], busy: false, liveRun: null, phase: null, phaseAt: 0,
      phaseElapsed: 0, filesOpen: false, fileSel: null, filesStamp: 0, sidebarOpen: false, announce: '',
      error: '', seed: null, renaming: null, renameText: '', ws: null, loadingProject: false,
      filesWidth: readWidth(),
    };
    this.closer = null; this.ticker = null; this.lastWake = 0; this.wsTimer = null; this.stampTimer = null;
  }

  // ─── lifecycle ────────────────────────────────────────────────────────────
  async componentDidMount() {
    this.refreshWs();
    this.wsTimer = setInterval(() => { if (!document.hidden) this.refreshWs(); }, 45000);
    this.ticker = setInterval(() => {
      const { busy, phase, phaseAt } = this.state;
      if (busy && phaseAt && (phase === 'waking' || phase === 'starting')) this.setState({ phaseElapsed: Math.round((Date.now() - phaseAt) / 1000) });
    }, 1000);
    await this.loadProjects();
    if (this.props.pid) this.open(this.props.pid);
  }

  componentDidUpdate(prev) {
    if (prev.pid !== this.props.pid) {
      if (this.props.pid) { if (String(this.state.project && this.state.project.id) !== String(this.props.pid)) this.open(this.props.pid); }
      else this.reset();
    }
  }

  componentWillUnmount() {
    if (this.closer) this.closer();
    clearInterval(this.ticker); clearInterval(this.wsTimer); clearTimeout(this.stampTimer);
  }

  reset() {
    if (this.closer) { this.closer(); this.closer = null; }
    this.setState({ project: null, items: [], busy: false, liveRun: null, phase: null, fileSel: null, error: '' });
  }

  // ─── data ─────────────────────────────────────────────────────────────────
  async refreshWs() {
    try {
      const ws = await getWorkspace();
      const was = this.state.ws && this.state.ws.sleep_state;
      this.setState({ ws });
      // images from the workspace that failed while it slept can load now
      if (ws.sleep_state === 'AWAKE' && was && was !== 'AWAKE') window.dispatchEvent(new Event('uai-workspace-awake'));
      if (this.props.onWsState) this.props.onWsState(ws.sleep_state || '');
    } catch (e) { /* the top bar keeps its last state */ }
  }

  async loadProjects() {
    try { this.setState({ projects: await listProjects() }); } catch (e) { this.setState({ projects: [], error: e.message }); }
  }

  open(pid) {
    // Creating a chat both changes the route and opens it; share one load, not two.
    if (this.opening && this.opening.pid === String(pid)) return this.opening.p;
    const p = this._open(pid).finally(() => { if (this.opening && this.opening.p === p) this.opening = null; });
    this.opening = { pid: String(pid), p };
    return p;
  }

  async _open(pid) {
    if (this.closer) { this.closer(); this.closer = null; }
    this.setState({ loadingProject: true, error: '', sidebarOpen: false });
    let data;
    try { data = await getProject(pid); } catch (e) { this.setState({ loadingProject: false, error: e.message }); return; }
    if (String(this.props.pid) !== String(pid)) return;            // navigated away meanwhile
    const live = data.live || {};
    const busy = BUSY.has(live.state);
    this.setState({ project: data, items: fromHistory(data.messages), busy, liveRun: busy ? live.run : null,
      phase: busy ? live.phase : null, phaseAt: busy ? Date.now() : 0, loadingProject: false,
      fileSel: null, filesStamp: this.state.filesStamp + 1 });
    document.title = `${data.title} — Uplevate`;
    this.closer = streamProject(pid, live.resume_after || live.seq || 0, (ev) => this.onEvent(pid, ev), () => {});
  }

  onEvent(pid, ev) {
    if (String(pid) !== String(this.state.project && this.state.project.id)) return;
    switch (ev.type) {
      case 'hello': {
        const busy = BUSY.has(ev.state);
        this.setState({ busy, liveRun: busy ? ev.run : null, phase: busy ? ev.phase : null });
        return;
      }
      case 'phase':
        this.setState((s) => ({ phase: ev.phase, busy: true, liveRun: ev.run || s.liveRun,
          phaseAt: s.phase === ev.phase ? s.phaseAt : Date.now() - (ev.elapsed || 0) * 1000,
          phaseElapsed: ev.elapsed || 0 }));
        return;
      case 'user':
        if (this.state.items.some((m) => m.role === 'user' && m.run != null && m.run === (ev.run_id || ev.run))) return;
        this.setState((s) => ({ items: applyEvent(s.items, ev), busy: true, liveRun: ev.run_id || ev.run, phase: s.phase || 'preparing', phaseAt: s.phaseAt || Date.now() }));
        return;
      case 'run_finished':
        window.dispatchEvent(new Event('uai-workspace-awake'));
        this.setState((s) => ({ items: applyEvent(s.items, ev), busy: false, liveRun: null, phase: null,
          announce: ev.status === 'done' ? 'Response complete' : `Response ${ev.status}`, filesStamp: s.filesStamp + 1 }));
        this.touchProject();
        this.refreshWs();
        return;
      case 'tool_update':
        if (ev.status === 'completed' && (ev.kind === 'file_write' || ev.kind === 'file_edit' || ev.kind === 'shell')) this.bumpFiles();
        break;
      default:
    }
    this.setState((s) => ({ items: applyEvent(s.items, ev) }));
  }

  bumpFiles() {
    if (!this.state.filesOpen) return;
    clearTimeout(this.stampTimer);
    this.stampTimer = setTimeout(() => this.setState((s) => ({ filesStamp: s.filesStamp + 1 })), 800);
  }

  touchProject() {
    const p = this.state.project; if (!p) return;
    const now = new Date().toISOString();
    this.setState((s) => ({ projects: (s.projects || []).map((x) => (x.id === p.id ? { ...x, updated_at: now } : x))
      .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at))) }));
  }

  // ─── actions ──────────────────────────────────────────────────────────────
  async send(text, attachments) {
    this.setState({ error: '' });
    let p = this.state.project;
    try {
      if (!p) {
        p = await createProject(titleFrom(text || (attachments[0] && attachments[0].name) || 'New chat'), 'auto');
        this.setState((s) => ({ projects: [p, ...(s.projects || [])] }));
        route('/p/' + p.id);
        await this.open(p.id);
      }
      // a chat created by attaching a file first is still called "New chat": name it now
      if (p.title === 'New chat' && text && !this.state.items.some((m) => m.role === 'user')) {
        const title = titleFrom(text);
        updateProject(p.id, { title }).catch(() => {});
        this.setState((s) => ({ project: s.project && s.project.id === p.id ? { ...s.project, title } : s.project,
          projects: (s.projects || []).map((x) => (x.id === p.id ? { ...x, title } : x)) }));
      }
      this.setState({ busy: true, phase: 'preparing', phaseAt: Date.now(), phaseElapsed: 0 });
      await sendMessage(p.id, text, attachments);
    } catch (e) {
      this.setState({ error: e.message, busy: false, phase: null });
    }
  }

  // A new chat needs a project before files can be uploaded into it.
  async ensureProject() {
    if (this.state.project) return this.state.project.id;
    const p = await createProject('New chat', 'auto');
    this.setState((s) => ({ projects: [p, ...(s.projects || [])] }));
    route('/p/' + p.id);
    await this.open(p.id);
    return p.id;
  }

  stop() { if (this.state.project) cancelTurn(this.state.project.id).catch(() => {}); }

  async answer(cbid, payload) {
    try {
      await answerCallback(this.state.project.id, { callback_id: cbid, ...payload });
      this.setState((s) => ({ items: markAnswered(s.items, cbid) }));
    } catch (e) { this.setState({ error: e.message }); }
  }

  prewake() {
    const ws = this.state.ws;
    if (!ws || ws.sleep_state !== 'SLEEPING' || Date.now() - this.lastWake < 60000) return;
    this.lastWake = Date.now();
    wakeWorkspace().then(() => this.refreshWs()).catch(() => {});
    if (this.props.onWsState) this.props.onWsState('WAKING');
  }

  openFile(path) {
    this.setState({ filesOpen: true, fileSel: path });
  }

  async rename(p) {
    const title = this.state.renameText.trim();
    this.setState({ renaming: null });
    if (!title || title === p.title) return;
    try {
      await updateProject(p.id, { title });
      this.setState((s) => ({ projects: s.projects.map((x) => (x.id === p.id ? { ...x, title } : x)),
        project: s.project && s.project.id === p.id ? { ...s.project, title } : s.project }));
    } catch (e) { this.setState({ error: e.message }); }
  }

  async archive(p) {
    if (!window.confirm(`Archive "${p.title}"? Its folder stays in your workspace.`)) return;
    try {
      await archiveProject(p.id);
      this.setState((s) => ({ projects: s.projects.filter((x) => x.id !== p.id) }));
      if (this.state.project && this.state.project.id === p.id) route('/');
    } catch (e) { this.setState({ error: e.message }); }
  }

  startResize(e) {
    e.preventDefault();
    const startX = e.clientX, startW = this.state.filesWidth;
    const move = (ev) => this.setState({ filesWidth: Math.min(Math.max(startW + (startX - ev.clientX), 300), Math.round(window.innerWidth * 0.7)) });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      try { localStorage.setItem('uai.filesWidth', String(this.state.filesWidth)); } catch (err) { /* */ } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  }

  // ─── view ─────────────────────────────────────────────────────────────────
  sidebar() {
    const { projects, project, renaming, renameText } = this.state;
    const active = project && project.id;
    return h('nav', { className: 'side', 'aria-label': 'Chats' },
      h('button', { type: 'button', className: 'new-chat', onClick: () => { route('/'); this.setState({ sidebarOpen: false }); } },
        h(Icon, { name: 'plus' }), ' New chat'),
      h('div', { className: 'side-list' },
        projects === null ? h('div', { className: 'side-skel' }, [0, 1, 2, 3].map((i) => h('span', { key: i })))
          : !projects.length ? h('p', { className: 'muted side-empty' }, 'Your chats appear here.')
          : groupProjects(projects).map(([label, xs]) => h('div', { key: label, className: 'side-group' },
            h('h4', null, label),
            xs.map((p) => renaming === p.id
              ? h('form', { key: p.id, className: 'side-rename', onSubmit: (e) => { e.preventDefault(); this.rename(p); } },
                h('input', { value: renameText, ref: (el) => el && el.focus(), 'aria-label': 'Chat name',
                  onInput: (e) => this.setState({ renameText: e.target.value }), onBlur: () => this.rename(p),
                  onKeyDown: (e) => { if (e.key === 'Escape') this.setState({ renaming: null }); } }))
              : h('div', { key: p.id, className: 'side-item' + (p.id === active ? ' on' : '') },
                h('a', { href: '/p/' + p.id, className: 'side-link', title: p.title }, p.title),
                h('span', { className: 'side-acts' },
                  h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Rename', title: 'Rename',
                    onClick: () => this.setState({ renaming: p.id, renameText: p.title }) }, h(Icon, { name: 'pencil', size: 14 })),
                  h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Archive', title: 'Archive',
                    onClick: () => this.archive(p) }, h(Icon, { name: 'archive', size: 14 })))))))));
  }

  render(props, s) {
    const pid = s.project && s.project.id;
    const title = s.project ? s.project.title : 'New chat';
    return h('div', { className: 'chat-app' + (s.sidebarOpen ? ' side-open' : '') + (s.filesOpen ? ' files-open' : ''),
      style: { '--files-w': s.filesWidth + 'px' } },
      this.sidebar(),
      h('div', { className: 'side-scrim', onClick: () => this.setState({ sidebarOpen: false }) }),
      h('main', { className: 'main' },
        h('header', { className: 'main-head' },
          h('button', { type: 'button', className: 'icon-btn only-mobile', 'aria-label': 'Open chats',
            onClick: () => this.setState({ sidebarOpen: true }) }, h(Icon, { name: 'menu', size: 18 })),
          h('h1', { className: 'main-title', title }, title),
          pid ? h('button', { type: 'button', className: 'btn ghost files-btn' + (s.filesOpen ? ' on' : ''),
            'aria-pressed': s.filesOpen ? 'true' : 'false',
            onClick: () => this.setState({ filesOpen: !s.filesOpen, filesStamp: s.filesStamp + 1 }) },
            h(Icon, { name: 'files' }), h('span', null, ' Files')) : null),
        s.error ? h('div', { className: 'banner err', role: 'alert' }, s.error,
          h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Dismiss', onClick: () => this.setState({ error: '' }) }, h(Icon, { name: 'close' }))) : null,
        s.loadingProject ? h('div', { className: 'feed-wrap' }, h('div', { className: 'feed' }, h('div', { className: 'feed-inner' },
          h('div', { className: 'skel' }, h('span', null), h('span', null), h('span', { className: 'short' }))))) :
        h(Feed, { items: s.items, pid, busy: s.busy, liveRun: s.liveRun, phase: s.phase, phaseElapsed: s.phaseElapsed,
          announce: s.announce, emptyTitle: s.project ? 'What should we work on?' : null,
          onAnswer: (id, pl) => this.answer(id, pl), onOpenFile: (p) => this.openFile(p),
          onRetry: (u) => this.send(u.content, u.attachments || []),
          onSuggest: (text) => this.setState({ seed: { text, at: Date.now() } }) }),
        h(Composer, { pid, busy: s.busy, seed: s.seed, autoFocus: true,
          ensureProject: () => this.ensureProject(),
          onSend: (t, a) => this.send(t, a), onStop: () => this.stop(), onPrewake: () => this.prewake(),
          hint: s.ws && s.ws.sleep_state === 'SLEEPING' ? 'Your workspace is asleep; it wakes when you send.' : '' })),
      s.filesOpen && pid ? h('div', { className: 'files-col' },
        h('div', { className: 'files-grip', onPointerDown: (e) => this.startResize(e), role: 'separator', 'aria-orientation': 'vertical', 'aria-label': 'Resize files panel' }),
        h(FilesPanel, { pid, selected: s.fileSel, stamp: s.filesStamp, onSelect: (p) => this.setState({ fileSel: p }),
          onClose: () => this.setState({ filesOpen: false }) })) : null);
  }
}

function readWidth() {
  try { return Math.max(300, parseInt(localStorage.getItem('uai.filesWidth') || '440', 10) || 440); } catch (e) { return 440; }
}
